import "./styles/listItem.less";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { IFGameItem } from "@/api/apiRsp/IFHomeRsp";
import useDialogManager from "@/utils/useDialogManager";
import { IconJqqd, IconWhz } from "@/assets/svg-icon";
import { useLocation, useNavigate } from "react-router";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import {
  useGlobalCurrentWallet,
  useGlobalOpenSearch,
  useGlobalState,
} from "@/store/commonStore";
import NiceModal from "@ebay/nice-modal-react";
import CommonPopup from "@/pages/commonPopup";
import { covertTranslationImagePath } from "@/Core/utils";
import { useBeforeSetting } from "@/store/beforeSetting";
import GameImageLoad from "@/pages/home/components/gameImageLoad/GameImageLoad";
import useScreenDesign from "@/Core/utils/hooks/useScreenDesign";
import { AmountDivisorConversion } from "@/Core/utils/currencyDealCenter";
import { useEnterGame, useGetCurrencyTypePlus } from "@/store/game";
import { feedback } from "@/utils/feedback";

interface ItemProps {
  data?: IFGameItem;
  onClick?: () => void;
  refresh?: boolean;
}

/**
 * 作者: nick
 * 时间: 2023/5/9
 * 说明: ***
 */

const ListItem = React.memo(
  forwardRef<HTMLDivElement, ItemProps>((Props, ref) => {
    const data = Props.data;
    const refresh = Props.refresh;
    const navigate = useNavigate();
    const location = useLocation();
    const { isLogin } = useGlobalState();
    const { openSignIn, openWallet, openFunPage, openGameIframe } =
      useDialogManager();
    const { t } = useTranslationPlus("ApiGame");
    const { t: tGameOriginal } = useTranslationPlus("GameOriginal");
    const [walletData] = useGlobalCurrentWallet();
    const [, setCloseSearch] = useGlobalOpenSearch();
    const isMobile = useScreenDesign();
    const { data: sitting } = useBeforeSetting();

    // 移动端API游戏进入相关
    const { data: gameCurrency } = useGetCurrencyTypePlus(
      isMobile ? walletData?.serverCurrencyType || walletData?.currencyType : null
    );
    const [gameCurrencyType, setGameCurrencyType] = useState({
      currencyType: "",
    });
    const { trigger: enterGameTrigger, isMutating: enterGameMutating } =
      useEnterGame();

    // 设置默认游戏货币
    useEffect(() => {
      if (isMobile && gameCurrency && gameCurrency?.length) {
        setGameCurrencyType({ currencyType: gameCurrency[0] });
      }
    }, [gameCurrency, isMobile]);

    /**
     * 返回 boolean  true 为需要弹窗显示 fun币的游戏， false 为不需要显示fun币游戏
     */
    const checkFunGame = useCallback((): boolean => {
      let needTip = false;
      const plats4FUN = sitting.plats4FUN;
      const lockCurrency = sitting.lockCurrency;
      // 当前限制的fun币的游戏配置判断-- 当前货币为FUN时
      if (walletData?.currencyType === lockCurrency) {
        needTip = true;
        if (sitting && plats4FUN && plats4FUN.length) {
          for (let i = 0; i < plats4FUN.length; i++) {
            if (plats4FUN[i] === data.platformCode) {
              needTip = false;
            }
          }
        }
      }
      return needTip;
    }, [sitting, walletData?.currencyType, data?.platformCode]);

    // 检查是否是API游戏
    const isApiGame = useCallback(() => {
      return data?.platformCode !== "iw"; // 假设iw是内置游戏的platformCode
    }, [data?.platformCode]);

    // 移动端启动API游戏
    const startMobileApiGame = useCallback(() => {
      if (enterGameMutating) return;

      enterGameTrigger({
        device: "h5",
        gameId: data.id,
        walletCurrency: walletData?.serverCurrencyType || walletData?.currencyType,
        webPlatform: "h5",
        currencyType: gameCurrencyType?.currencyType,
      })
        .then((res) => {
          const isLiveGame = data.type && data.type[0] === "5";
          openGameIframe(res?.gameUrl, isLiveGame);
        })
        .catch((error) => {
          if (
            error &&
            error?.data &&
            error?.data?.currentGameId &&
            error?.data?.currentGameId !== data.id
          ) {
            // 在其它游戏中
            NiceModal.show(CommonPopup, {
              ontentText: t("OtherTips", {
                gameName: error.data.currentGameName,
              }),
              rightBtnText: tGameOriginal("Confirm"),
              rightCallback: ({ remove }) => {
                remove();
                navigate("/game/" + error.data.currentGameId);
              },
            });
            return;
          }
          feedback.error({
            content: `${t(error?.code)}`,
            duration: 1000,
          });
        });
    }, [
      enterGameMutating,
      enterGameTrigger,
      data.id,
      data.type,
      walletData?.currencyType,
      gameCurrencyType?.currencyType,
      openGameIframe,
      t,
      navigate,
    ]);

    // 缓存当前余额计算
    const currentBalance = useMemo(() => {
      return AmountDivisorConversion(walletData?.allBalance, {
        currencyType: walletData?.currencyType,
        serverCurrencyType: walletData?.serverCurrencyType,
      });
    }, [walletData?.allBalance, walletData?.currencyType, walletData?.serverCurrencyType]);

    // 缓存图片路径
    const imagePath = useMemo(() => {
      return covertTranslationImagePath(data?.id);
    }, [data?.id]);

    const onClick = useCallback(() => {
      if (!isLogin) {
        openSignIn();
        return;
      }
      Props.onClick && Props.onClick();
      if (data.status == 3) {
        feedback.info(t("GameStatusTip3"));
        return;
      }
      if (data.status == 4) {
        feedback.info(t("GameStatusTip4"));
        return;
      }

      if (checkFunGame()) {
        openFunPage();
        return;
      }

      if (currentBalance >= data.goldRequire) {
        setCloseSearch(false);
        if (isMobile && isApiGame()) {
          // 移动端API游戏直接启动
          startMobileApiGame();
        } else {
          // PC端所有游戏或移动端内置游戏：使用navigate
          navigate("/game/" + data.id, { state: { refresh: refresh } });
        }

        if (location.pathname.indexOf("game") != -1) {
          const ele = document.getElementById("game-container");
          ele && ele.scrollIntoView();
        }
      } else {
        NiceModal.show(CommonPopup, {
          ontentText: t("CanNotMoney"),
          leftBtnText: tGameOriginal("Cancle"),
          rightBtnText: tGameOriginal("Confirm"),
          rightCallback: ({ remove }) => {
            remove();
            openWallet();
          },
        });
      }
    }, [
      isLogin,
      data.status,
      data.id,
      data.goldRequire,
      Props.onClick,
      checkFunGame,
      currentBalance,
      isMobile,
      isApiGame,
      startMobileApiGame,
      navigate,
      refresh,
      location.pathname,
      openSignIn,
      openFunPage,
      openWallet,
      setCloseSearch,
      t,
    ]);

    return (
      <div ref={ref} className={"game-item"} onClick={onClick}>
        <div className={"def-item--parent"}>
          <div className={"game-image-default"} />

          <GameImageLoad src={imagePath} />

          {data && data.status == 4 && (
            <div className={"def-item-jqqd"}>
              <IconJqqd />
              <span>{t("GameStatus4")}</span>
            </div>
          )}
          {data && data.status == 3 && (
            <div className={"def-item-whz"}>
              <IconWhz />
              <span>{t("GameStatus3")}</span>
            </div>
          )}
        </div>
      </div>
    );
  })
);

ListItem.displayName = "ListItem";

export default ListItem;
